import React from 'react';
import styled from '@emotion/styled';
import { Collapse, Divider, Space } from 'antd';
import { InfoLine } from '../Elements';
import { Paragraph } from 'app/components/Typography/Paragraph';
import { urlify } from 'app/utils';
import { isEmpty } from 'lodash';

const StyledPanel = styled(Collapse.Panel, {
  shouldForwardProp: (prop) => prop !== 'closeableHeight' && prop !== 'expanded'
})<{ closeableHeight: number; expanded: boolean }>`
  border: ${(prop) =>
    prop.expanded ? '1.25px solid #669EFF' : '1.25px solid #c4c8d0'} !important;
  border-radius: 3px;
  .ant-collapse-header {
    background: #fff;
  }
  .ant-collapse-content {
    display: block;
    min-height: ${(props) => props.closeableHeight}px;
  }
  .ant-collapse-content-inactive {
    background: #fff;
    display: unset !important;
    opacity: 1 !important;
    .ant-collapse-content-box {
      max-height: ${(props) => props.closeableHeight}px !important;
      transition: max-height 0.5s ease-out;
      overflow: hidden;
    }
  }
  .ant-collapse-content {
    border-top: 0px;
  }
  .ant-collapse-content-box {
    padding-top: 0px;
    background: #fff;
  }
`;

const StyledInfoLine = styled(InfoLine)`
  font-size: 12px;
  font-weight: 500;
  text-align: left;
  padding-top: 2px;
  > .ant-space-item:last-child {
  }
`;

type SectionCriteriaProps = {
  title: React.ReactNode;
  sectionLabel: string;
  sectionKey: string; // Create Key for collapse/expand
  // sectionName: string; // Section Name to map field and map value.
  remark: string;
  expanded?: boolean;
  fields: any[];
  values: any;
  numOfCompletedField: number;
  numOfRequiredField: number;
  section: any;
};
const SectionCriteria: React.FC<SectionCriteriaProps> = ({
  title,
  sectionKey,
  remark,
  expanded,
  sectionLabel,
  fields,
  values,
  numOfRequiredField,
  numOfCompletedField,
  section,
  ...rest
}) => {
  const remarkRef = React.useRef<HTMLDivElement>();
  const [closeableHeight, setCloseableHeight] = React.useState(0);
  React.useEffect(() => {
    if (remarkRef.current) {
      setCloseableHeight(remarkRef.current.clientHeight + 36);
    }
  }, []);
  const sectionFields = React.useMemo(() => {
    if (['packingMarking'].includes(section?.name)) {
      return section.fields
        ?.map((sectionField: any) => {
          const { enabled = true } = sectionField;
          const subSection = fields.find(
            ({ dataIndex }) => dataIndex === sectionField.name
          );
          if (!enabled) {
            return null;
          }
          return subSection;
        })
        .filter((field: unknown) => !!field);
    }
    return fields;
  }, [section, fields]);

  return (
    <StyledPanel
      expanded={!!expanded}
      closeableHeight={closeableHeight}
      forceRender
      header={<>{title}</>}
      key={sectionKey}
      {...rest}
    >
      <Divider style={{ marginTop: 0, marginBottom: 12 }}></Divider>
      {['packingMarking', 'onSiteTest'].includes(section?.name) ? null : (
        <>
          <InfoLine
            ref={remarkRef as any}
            label="Remark"
            value={
              !isEmpty(remark) ? (
                <Paragraph style={{ marginBottom: 0 }}>
                  <p
                    style={{ marginBottom: 0 }}
                    dangerouslySetInnerHTML={{
                      __html: `<div>${urlify(remark || 'N/A')}</div>`
                    }}
                  ></p>
                </Paragraph>
              ) : (
                'N/A'
              )
            }
          ></InfoLine>
          <Divider style={{ marginTop: 12, marginBottom: 12 }}></Divider>
        </>
      )}

      <Space direction="vertical" style={{ width: '100%' }}>
        <InfoLine
          label={sectionLabel}
          value={
            <StyledInfoLine
              label="Progress:"
              value={
                section?.overallProgress ||
                `${numOfCompletedField}/${numOfRequiredField}`
              }
            ></StyledInfoLine>
          }
        ></InfoLine>
        {(sectionFields || []).map((field: any) => {
          if (field.render)
            return field.render?.(values, field, fields, sectionKey);
          return null;
        })}
      </Space>
    </StyledPanel>
  );
};
export default SectionCriteria;
